import { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Check, Calendar, Copy, Download, Camera } from 'lucide-react';
import Button from '../components/Button';
import { useFormData } from '../context/FormDataContext';
import { motion } from 'framer-motion';
import html2canvas from 'html2canvas';

// Event time mapping — confirm with organizers before going live
const getEventDateTime = (eventType: string): string => {
  const type = eventType.toLowerCase();
  if (type.includes('slack')) {
    return 'July 4th, 2026 — Slack at 7:00 AM';
  }
  if (type.includes('july 3') || type.includes('3rd')) {
    return 'July 3rd, 2026 at 7:45 PM';
  }
  if (type.includes('july 4') || type.includes('4th')) {
    return 'July 4th, 2026 at 7:45 PM';
  }
  return eventType;
};

const ConfirmationPage = () => {
  const { type } = useParams<{ type: string }>();
  const navigate = useNavigate();
  const { formData } = useFormData();
  const confirmationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!formData.type) {
      navigate('/');
    }
  }, [formData, navigate]);

  if (!formData.type) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <Button variant="primary" onClick={() => navigate('/')}>
          Return to Home
        </Button>
      </div>
    );
  }

  const getConfirmationTitle = () => {
    switch (type) {
      case 'contestant':
        return 'Contestant Registration Confirmed';
      case 'ticket':
        return 'Ticket Purchase Confirmed';
      case 'queen':
        return 'Rodeo Queen Application Submitted';
      default:
        return 'Submission Confirmed';
    }
  };

  const getConfirmationMessage = () => {
    switch (type) {
      case 'contestant':
        return `Thank you for registering as a contestant for the ${formData.event?.event_name}! Your entry fee of $${formData.event?.price} will be due in person, in CASH only, before the rodeo begins. We are not accepting Venmo, checks, or credit cards. Please come prepared with cash and arrive early so you have time to check in and pay your fees.`;
      case 'ticket':
        if (formData.ticketType === 'multiple') {
          return `Thank you for your ticket purchase! Your tickets are confirmed for the Salina Independence Day Rodeo.`;
        }
        return `Thank you for your ticket purchase! Your ${formData.quantity}x ${formData.ticketName} tickets are confirmed for the Salina Independence Day Rodeo.`;
      case 'queen':
        return `Thank you for applying to be our next Rodeo Queen! Your application has been received and is under review. Our committee will be in touch soon with next steps in the selection process.`;
      default:
        return 'Thank you for your submission. We have received your information and will be in touch soon.';
    }
  };

  const getConfirmationDetails = () => {
    switch (type) {
      case 'contestant':
        return [
          { label: 'Name', value: `${formData.first_name} ${formData.last_name}` },
          { label: 'Event', value: formData.event?.event_name },
          { label: 'Date/Time', value: formData.event?.event_type ? getEventDateTime(formData.event.event_type) : '' },
          { label: 'Entry Fee', value: `$${formData.event?.price} (CASH only)` },
          { label: 'Email', value: formData.email },
          { label: 'Phone', value: formData.phone_number },
          { label: 'Confirmation #', value: formData.confirmation_number || 'Pending' },
        ];
      case 'ticket':
        if (formData.ticketType === 'multiple') {
          const details = [
            { label: 'Name', value: formData.full_name || formData.fullName },
            { label: 'Email', value: formData.email },
            { label: 'Total', value: `$${formData.totalAmount?.toFixed(2)}` },
            { label: 'Status', value: 'Confirmed' },
            { label: 'Confirmation #', value: formData.confirmation_number || 'Pending' },
          ];

          if (formData.adultQuantity > 0) {
            details.splice(2, 0, {
              label: 'Adult Tickets',
              value: `${formData.adultQuantity}`
            });
          }

          if (formData.childQuantity > 0) {
            details.splice(formData.adultQuantity > 0 ? 3 : 2, 0, {
              label: 'Child Tickets',
              value: `${formData.childQuantity}`
            });
          }

          return details;
        }

        return [
          { label: 'Name', value: formData.full_name || formData.fullName },
          { label: 'Ticket Type', value: formData.ticketName },
          { label: 'Quantity', value: formData.quantity },
          { label: 'Total', value: `$${formData.totalAmount?.toFixed(2)}` },
          { label: 'Email', value: formData.email },
          { label: 'Status', value: 'Confirmed' },
          { label: 'Confirmation #', value: formData.confirmation_number || 'Pending' },
        ];
      case 'queen':
        return [
          { label: 'Name', value: formData.full_name || formData.fullName },
          { label: 'Age', value: formData.age },
          { label: 'Email', value: formData.email },
          { label: 'Phone', value: formData.phone },
          { label: 'Confirmation #', value: formData.confirmation_number || 'Pending' },
        ];
      default:
        return [];
    }
  };

  const downloadConfirmation = async () => {
    if (confirmationRef.current) {
      try {
        const downloadButton = document.getElementById('download-button');
        const returnButton = document.getElementById('return-button');

        if (downloadButton) downloadButton.style.display = 'none';
        if (returnButton) returnButton.style.display = 'none';

        const canvas = await html2canvas(confirmationRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: false,
          height: confirmationRef.current.scrollHeight,
          width: confirmationRef.current.scrollWidth,
        });

        if (downloadButton) downloadButton.style.display = '';
        if (returnButton) returnButton.style.display = '';

        const image = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.download = `rodeo-confirmation-${formData.confirmation_number || 'receipt'}.png`;
        link.href = image;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Error generating screenshot:', error);
        alert('Sorry, there was an error saving your confirmation. Please try again or take a screenshot manually.');
      }
    }
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <div className="max-w-2xl mx-auto text-center">
      <div ref={confirmationRef} className="p-8 bg-white">
        <motion.div
          className="mb-6 flex flex-col items-center"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <Check size={32} className="text-green-600" />
          </div>

          <h1 className="text-3xl md:text-4xl font-heading font-bold text-primary-900">
            {getConfirmationTitle()}
          </h1>
        </motion.div>

        {/* Screenshot reminder */}
        {type === 'contestant' && (
          <motion.div
            className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.4 }}
          >
            <div className="flex items-center justify-center">
              <Camera size={20} className="text-green-700 mr-2" />
              <p className="text-green-800 font-semibold text-sm">
                Please save a screenshot of this confirmation. You will need it to check in at the rodeo.
              </p>
            </div>
          </motion.div>
        )}

        <motion.p
          className="text-gray-700 mb-8 max-w-lg mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          {getConfirmationMessage()}
        </motion.p>

        <motion.div
          className="bg-white rounded-lg p-6 shadow-md mb-8 text-left border"
          variants={container}
          initial="hidden"
          animate="show"
        >
          <h3 className="text-xl font-heading font-semibold text-primary-800 mb-4 text-center">
            Confirmation Details
          </h3>

          <dl className="space-y-3">
            {getConfirmationDetails().map((detail, index) => (
              <motion.div key={index} className="flex justify-between" variants={item}>
                <dt className="font-medium text-gray-600">{detail.label}:</dt>
                <dd className="text-gray-800">{detail.value}</dd>
              </motion.div>
            ))}
          </dl>
        </motion.div>

        {type === 'contestant' && (
          <motion.div
            className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <h4 className="font-semibold text-yellow-800 mb-2">Payment Reminder</h4>
            <p className="text-yellow-700 text-sm">
              Your entry fee of ${formData.event?.price} will be due in person, in CASH only, before the rodeo begins.
              We are not accepting Venmo, checks, or credit cards. Please come prepared with cash and arrive early
              so you have time to check in and pay your fees.
            </p>
          </motion.div>
        )}

        <motion.div
          className="mb-8 space-y-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <div className="flex items-center justify-center text-gray-700 space-x-2">
            <Calendar size={18} />
            <span>
              {formData.event?.event_type
                ? getEventDateTime(formData.event.event_type)
                : 'Event Date: July 3-4, 2026'
              }
            </span>
          </div>

          <div className="text-sm text-gray-500 mt-4">
            <p>
              Confirmation ID: {formData.confirmation_number || formData.id}
            </p>
            <div className="flex justify-center mt-2">
              <button
                className="text-primary-600 hover:text-primary-800 inline-flex items-center"
                onClick={() => {
                  navigator.clipboard.writeText(formData.confirmation_number || formData.id);
                  alert('Confirmation ID copied to clipboard');
                }}
              >
                <Copy size={14} className="mr-1" /> Copy ID
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div
        className="space-y-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.5 }}
      >
        <Button
          id="download-button"
          variant="secondary"
          onClick={downloadConfirmation}
          icon={<Download size={18} />}
          className="mr-4"
        >
          Save as PNG
        </Button>

        <Button
          id="return-button"
          variant="primary"
          onClick={() => navigate('/')}
        >
          Return to Home
        </Button>
      </motion.div>
    </div>
  );
};

export default ConfirmationPage;
